import React, { useState, useEffect, useRef } from "react";

// ── Custom hook for counter animation ──────────────────────────────────────
export function useCounter(target, duration = 1500) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started.current) {
                    started.current = true;
                    const start = performance.now();
                    const tick = (now) => {
                        const elapsed = now - start;
                        const progress = Math.min(elapsed / duration, 1);
                        setCount(Math.floor(progress * target));
                        if (progress < 1) requestAnimationFrame(tick);
                        else setCount(target);
                    };
                    requestAnimationFrame(tick);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [target, duration]);

    return { count, ref };
}

// ── Single number animator component ──────────────────────────────────────
export function SingleNumberCounter({ target, isFloat, duration = 1800 }) {
    const { count, ref } = useCounter(target, duration);
    return (
        <span ref={ref}>
            {isFloat ? count.toFixed(1) : Math.floor(count)}
        </span>
    );
}

// ── Flexible animated counter (supports multiple numbers) ───────────────────
export default function AnimatedCounter({ value, className }) {
    if (typeof value !== 'string') value = String(value);

    // Split by numbers (including floats) while keeping delimiters
    // e.g., "10+5" -> ["", "10", "+", "5", ""]
    const parts = value.split(/(\d+(?:\.\d+)?)/);

    return (
        <span className={className}>
            {parts.map((part, i) => {
                if (/^\d+(?:\.\d+)?$/.test(part)) {
                    const numeric = parseFloat(part);
                    const isFloat = part.includes('.');
                    return (
                        <SingleNumberCounter
                            key={i}
                            target={numeric}
                            isFloat={isFloat}
                        />
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </span>
    );
}
